<template>
  <div>
    <v-card-text>
      <v-text-field
        v-if="searchField"
        v-model="search"
        append-inner-icon="mdi-magnify"
        label="Search"
        clearable
        hide-details
      ></v-text-field>
    </v-card-text>

    <v-data-table
      v-model="selected"
      v-model:page="page"
      v-model:items-per-page="itemsPerPage"
      :items="items"
      :search="search"
      :headers="columns"
      :loading="loading"
      :item-value="itemKey"
      :show-select="showSelect"
      :show-expand="showExpand"
      :single-expand="singleExpand"
      @update:selected="$emit('selectionChanged', $event)"
      @click:row="(e, row) => emit('rowClicked', row.item)"
    >
      <template #item.actions="{ item }">
        <v-btn
          v-if="showEditPerTableLine"
          icon="mdi-pencil"
          size="small"
          variant="tonal"
          @click.stop="$emit('edit', item)"
        >
          <v-icon> mdi-pencil </v-icon>
        </v-btn>
        <v-btn
          v-if="showRemovePerTableLine"
          icon="mdi-trash-can-outline"
          size="small"
          variant="tonal"
          @click.stop="$emit('remove', item)"
        >
          <v-icon> mdi-trash-can-outline </v-icon>
        </v-btn>
      </template>

      <template v-if="showExpand" #expanded-row="{ item, columns }">
        <tr>
          <td :colspan="columns.length">
            <ul class="expanded-list">
              <li v-for="(value, i) in expandedValues(item)" :key="i">
                {{ value }}
              </li>
            </ul>
          </td>
        </tr>
      </template>

      <template #no-data>
        <div class="text-center py-4 text-medium-emphasis">
          <v-icon class="mr-2">mdi-information</v-icon>
          Nothing to display
        </div>
      </template>
      <template v-slot:bottom>
        <div class="text-center pt-2">
          <v-pagination v-model="page" :length="pageCount"></v-pagination>
        </div>
      </template>
    </v-data-table>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, watch } from "vue";
import type { Entity, TableColumn } from "@/types";

const props = defineProps<{
  items: Entity[];
  columns: TableColumn[];
  searchField: boolean;
  showExpand?: boolean;
  showSelect?: boolean;
  singleExpand?: boolean;
  showRemovePerTableLine?: boolean;
  showEditPerTableLine?: boolean;
  expandedProperty?: string;
  loading: boolean;
}>();

const emit = defineEmits(["selectionChanged", "edit", "remove", "rowClicked"] as const);

const search = ref("");
const page = ref(1);
const itemsPerPage = ref(10);

const pageCount = computed(() => {
  return Math.ceil(props.items.length / itemsPerPage.value);
});
const itemKey = "id";

const selected = ref<Entity[]>([]);

watch(selected, (val) => {
  emit("selectionChanged", val);
});

const expandedValues = (item: Entity): string[] => {
  const value = item[props.expandedProperty];

  if (!value) return [`No ${props.expandedProperty}`];

  if (Array.isArray(value)) {
    return value.length
      ? value.map((v) => v?.name ?? String(v))
      : [`No ${props.expandedProperty}`];
  }

  return [String(value)];
};
</script>

<style scoped>
.expanded-list {
  list-style: none;
  padding-left: 0;
}
</style>
